/**
 * AP-11-E (E4) — „Einstellungen → Speicher": was belegt ist, was erlaubt ist,
 * und wohin der Platz gegangen ist.
 *
 * Eine Anzeige, keine Maske: Das Kontingent setzt der Betreiber, nicht der
 * Mandant. Darunter steht seit E5 der Abschnitt „Eigener Speicher" — für den, der
 * den Baustein gebucht hat und ihn einrichten darf. Wer ihn nicht hat und knapp
 * wird, bekommt den Hinweis, dass es ihn gibt.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, HardDrive } from 'lucide-react'
import { formCard, pageNarrow, pageTitle, cardTitle } from '@/lib/form-styles'
import { fehlertext } from '@/lib/fehlertext'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { ladeSpeicher, speicherProzent, speicherText, useSpeicher, type SpeicherUebersicht } from '@/lib/speicher'
import { EigenerSpeicher } from '../components/EigenerSpeicher'

export default function StoragePage() {
  const { t } = useTranslation()
  const [stand, setStand] = useState<SpeicherUebersicht | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const setze = useSpeicher((s) => s.setze)

  const user = useAuthStore((s) => s.user)
  const hasModule = useAuthStore((s) => s.hasModule)
  const eigenGebucht = hasModule('OWN_STORAGE')
  const darfEinrichten = eigenGebucht && erfuelltRecht('storage.own.manage', user?.permissions)
  const darfModuleSehen = erfuelltRecht('core.module.read', user?.permissions)

  const lade = useCallback(() => {
    ladeSpeicher()
      .then((s) => {
        setStand(s)
        // Der Hinweis im Rahmen zeigt denselben Stand — nicht den von der Anmeldung.
        setze(s)
      })
      .catch((e) => setFehler(fehlertext(e, t)))
  }, [setze, t])

  useEffect(lade, [lade])

  return (
    <div className={pageNarrow}>
      <div>
        <div className="flex items-center gap-3">
          <HardDrive size={24} className="text-primary" />
          <h1 className={pageTitle}>{t('storage.titel')}</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{t('storage.untertitel')}</p>
      </div>

      {fehler && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>
      )}

      {stand && (
        <>
          <Belegung stand={stand} />
          <Bereiche stand={stand} />
        </>
      )}

      {darfEinrichten && <EigenerSpeicher onGeaendert={lade} />}
      {stand && !eigenGebucht && stand.level !== 'OK' && (
        <p className="text-sm text-muted-foreground">
          {t('storage.eigen.anfragen')}{' '}
          {darfModuleSehen && (
            <Link to="/settings/modules" className="font-medium text-primary hover:underline">
              {t('storage.eigen.anfragenLink')}
            </Link>
          )}
        </p>
      )}
    </div>
  )
}

function Belegung({ stand }: { stand: SpeicherUebersicht }) {
  const { t } = useTranslation()
  const prozent = speicherProzent(stand)
  const ohneGrenze = stand.limitBytes === null

  return (
    <section className={formCard} aria-labelledby="speicher-belegung">
      <h2 id="speicher-belegung" className={cardTitle}>
        {t('storage.belegung')}
      </h2>

      <p className="mt-3 text-2xl font-semibold text-foreground">
        {ohneGrenze
          ? speicherText(stand.usedBytes)
          : t('storage.belegtVon', { belegt: speicherText(stand.usedBytes), grenze: speicherText(stand.limitBytes!) })}
      </p>

      {!ohneGrenze && (
        <>
          <div
            className="mt-3 h-2.5 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={prozent}
            aria-label={t('storage.belegung')}
          >
            <div
              className={`h-full ${stand.level === 'FULL' ? 'bg-destructive' : stand.level === 'WARN' ? 'bg-amber-500' : 'bg-primary'}`}
              style={{ width: `${prozent}%` }}
            />
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('storage.prozent', { prozent })} ·{' '}
            {t(stand.limitSource === 'CUSTOM' ? 'storage.grenzeVereinbart' : 'storage.grenzePlan')}
          </p>
        </>
      )}
      {ohneGrenze && (
        <p className="mt-2 text-sm text-muted-foreground">
          {t(stand.limitSource === 'OWN' ? 'storage.eigen.ohneKontingent' : 'storage.ohneGrenze')}
        </p>
      )}

      {stand.level !== 'OK' && (
        <div
          className={`mt-4 flex items-start gap-3 rounded-md border px-4 py-3 text-sm text-foreground ${
            stand.level === 'FULL' ? 'border-destructive/30 bg-destructive/10' : 'border-amber-500/30 bg-amber-500/10'
          }`}
        >
          <AlertTriangle
            size={18}
            className={`mt-0.5 shrink-0 ${stand.level === 'FULL' ? 'text-destructive' : 'text-amber-600 dark:text-amber-400'}`}
          />
          <div>
            <p className="font-medium">{t(stand.level === 'FULL' ? 'storage.voll.titel' : 'storage.knapp.titel')}</p>
            <p className="mt-1 text-muted-foreground">{t(stand.level === 'FULL' ? 'storage.voll.text' : 'storage.knapp.text')}</p>
          </div>
        </div>
      )}

      {!stand.storageReady && (
        <p className="mt-4 text-sm text-destructive">{t('storage.nichtBereit')}</p>
      )}
    </section>
  )
}

function Bereiche({ stand }: { stand: SpeicherUebersicht }) {
  const { t } = useTranslation()
  const groesste = Math.max(1, ...stand.areas.map((b) => b.bytes))

  return (
    <section className={formCard} aria-labelledby="speicher-bereiche">
      <h2 id="speicher-bereiche" className={cardTitle}>
        {t('storage.bereiche.titel')}
      </h2>
      {stand.areas.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">{t('storage.bereiche.leer')}</p>
      ) : (
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <th className="py-2 pr-3">{t('storage.bereiche.bereich')}</th>
              <th className="py-2 pr-3 text-right">{t('storage.bereiche.dateien')}</th>
              <th className="py-2 pr-3 text-right">{t('storage.bereiche.groesse')}</th>
              <th className="w-2/5 py-2" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {stand.areas.map((b) => (
              <tr key={`${b.module}:${b.countsToQuota}`} className="border-b border-border/60 last:border-0">
                <td className="py-2 pr-3 text-foreground">
                  {t(`storage.bereiche.namen.${b.module}`, { defaultValue: b.module })}
                  {!b.countsToQuota && (
                    <span className="ml-2 text-xs text-muted-foreground">{t('storage.bereiche.zaehltNicht')}</span>
                  )}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums text-foreground">
                  {new Intl.NumberFormat('de-DE').format(b.files)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums text-foreground">{speicherText(b.bytes)}</td>
                <td className="py-2">
                  <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full ${b.countsToQuota ? 'bg-primary' : 'bg-muted-foreground/40'}`}
                      style={{ width: `${Math.max(2, Math.round((b.bytes / groesste) * 100))}%` }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
